import { useState } from "react";
import { IonCheckbox, IonItem, IonList } from "@ionic/react";
import {
  LocalStorageFilterValueTypes,
  RequestLengthFilterTypes,
  RequestTypeFilterTypes,
  StartingDateFilterTypes,
  ThreadedData,
} from "src/components/molecules/FilterBar/types";
import { Button, Icon } from "swing-components";

import { isTempStorageChanged, translate } from "~utils";

type FilterModalProps = ThreadedData & { onDismiss: () => void };

export function FilterModal(props: FilterModalProps) {
  const { ls, setLs, onDismiss } = props;

  const startingOptions: Array<{
    label: string;
    subText?: string;
    value: StartingDateFilterTypes;
  }> = [
    {
      label: translate("FILTER_MODAL_SOON_LABEL"),
      subText: translate("FILTER_MODAL_SOON_SUB"),
      value: "soon",
    },
    { label: translate("FILTER_MODAL_THIS_WEEK_LABEL"), value: "this-week" },
    { label: translate("FILTER_MODAL_NEXT_WEEK_LABEL"), value: "next-week" },
  ];

  const requestLengthOptions: Array<{
    label: string;
    subText?: string;
    value: RequestLengthFilterTypes;
  }> = [
    { label: translate("FILTER_MODAL_SD_LABEL"), value: "single-day" },
    { label: translate("FILTER_MODAL_MD_LABEL"), value: "multi-day" },
    { label: translate("FILTER_MODAL_LT_LABEL"), value: "long-term" },
  ];

  const requestTypeOptions: Array<{
    label: string;
    subText?: string;
    value: RequestTypeFilterTypes;
  }> = [
    {
      label: translate("FILTER_MODAL_WORKED_BEFORE_LABEL"),
      subText: translate("FILTER_MODAL_WORKED_BEFORE_SUB"),
      value: "worked-before",
    },
    {
      label: translate("FILTER_MODAL_NEW_TODAY_LABEL"),
      subText: translate("FILTER_MODAL_NEW_TODAY_SUB"),
      value: "new-today",
    },
  ];

  const [startingSelection, setStartingSelection] = useState<Array<StartingDateFilterTypes>>(
    ls.startingDate,
  );
  const [requestLengthSelection, setRequestLengthSelection] = useState<
    Array<RequestLengthFilterTypes>
  >(ls.requestLength);
  const [requestTypeSelection, setRequestTypeSelection] = useState<Array<RequestTypeFilterTypes>>(
    ls.requestType,
  );

  const clearForm = () => {
    setStartingSelection([]);
    setRequestLengthSelection([]);
    setRequestTypeSelection([]);
  };

  const formState: LocalStorageFilterValueTypes = {
    startingDate: startingSelection,
    requestType: requestTypeSelection,
    requestLength: requestLengthSelection,
  };

  const isClearButtonEnabled =
    startingSelection.length > 0 ||
    requestLengthSelection.length > 0 ||
    requestTypeSelection.length > 0;

  const isApplyButtonEnabled = isTempStorageChanged(formState, ls);

  return (
    <div
      css={{
        position: "relative",
        overflow: "scroll",
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div
        css={{
          position: "sticky",
          top: 0,
          padding: "32px 16px 16px 16px",
          background: "white",
          display: "flex",
          justifyContent: "space-between",
          zIndex: 999,
        }}
      >
        <span css={{ fontSize: 20 }}>{translate("FILTER_MODAL_HEADER")}</span>
        <div css={{ cursor: "pointer" }} onClick={onDismiss}>
          <Icon name="Close" color="var(--swing-blue500)" />
        </div>
      </div>
      <div css={{ padding: "16px", paddingTop: "0px", flex: 1 }}>
        {/* Starting options */}
        <IonList>
          <p css={{ fontWeight: "bold", margin: "0 0 8px 0" }}>
            {translate("FILTER_MODAL_STARTING_HEADER")}
          </p>
          {startingOptions.map((item, index) => (
            <IonItem
              key={item.label}
              lines={index === startingOptions.length - 1 ? "none" : undefined}
            >
              <IonCheckbox
                labelPlacement="end"
                justify="start"
                css={{ padding: "16px 24px" }}
                value={item.label}
                checked={startingSelection.includes(item.value)}
                onClick={() => {
                  setStartingSelection((old) =>
                    old.includes(item.value)
                      ? old.filter((s) => s !== item.value)
                      : [...old, item.value],
                  );
                }}
              >
                <p css={{ margin: 0 }}>{item.label}</p>
                <span css={{ fontSize: 12 }}>{item?.subText}</span>
              </IonCheckbox>
            </IonItem>
          ))}
        </IonList>

        {/* Request Length Options */}
        <IonList>
          <p css={{ fontWeight: "bold", margin: "0 0 8px 0" }}>
            {translate("FILTER_MODAL_REQUEST_LENGTH_HEADER")}
          </p>
          {requestLengthOptions.map((item, index) => (
            <IonItem
              key={item.label}
              lines={index === requestLengthOptions.length - 1 ? "none" : undefined}
            >
              <IonCheckbox
                labelPlacement="end"
                justify="start"
                css={{ padding: "16px 24px" }}
                value={item.label}
                checked={requestLengthSelection.includes(item.value)}
                onClick={() => {
                  setRequestLengthSelection((old) =>
                    old.includes(item.value)
                      ? old.filter((s) => s !== item.value)
                      : [...old, item.value],
                  );
                }}
              >
                <p css={{ margin: 0 }}>{item.label}</p>
                <span css={{ fontSize: 12 }}>{item?.subText}</span>
              </IonCheckbox>
            </IonItem>
          ))}
        </IonList>

        {/* Request Type Options */}
        <IonList>
          <p css={{ fontWeight: "bold", margin: "0 0 8px 0" }}>
            {translate("FILTER_MODAL_TYPE_HEADER")}
          </p>
          {requestTypeOptions.map((item, index) => (
            <IonItem
              key={item.label}
              lines={index === requestTypeOptions.length - 1 ? "none" : undefined}
            >
              <IonCheckbox
                labelPlacement="end"
                justify="start"
                css={{ padding: "16px 24px" }}
                value={item.label}
                checked={requestTypeSelection.includes(item.value)}
                onClick={() => {
                  setRequestTypeSelection((old) =>
                    old.includes(item.value)
                      ? old.filter((s) => s !== item.value)
                      : [...old, item.value],
                  );
                }}
              >
                <p css={{ margin: 0 }}>{item.label}</p>
                <span css={{ fontSize: 12 }}>{item?.subText}</span>
              </IonCheckbox>
            </IonItem>
          ))}
        </IonList>
      </div>
      <div
        css={{
          position: "sticky",
          bottom: 0,
          padding: "16px",
          background: "white",
          display: "flex",
          gap: 16,
        }}
      >
        <div css={{ flex: 1 }}>
          <Button
            type="tertiary"
            status={isClearButtonEnabled ? "enabled" : "disabled"}
            onClick={() => {
              clearForm();
            }}
            isFluid
          >
            {translate("FILTER_MODAL_CLEAR_BUTTON")}
          </Button>
        </div>
        <div css={{ flex: 1 }}>
          <Button
            status={isApplyButtonEnabled ? "enabled" : "disabled"}
            onClick={() => {
              setLs({
                requestLength: requestLengthSelection,
                requestType: requestTypeSelection,
                startingDate: startingSelection,
              });
              onDismiss();
            }}
            type="primary"
            isFluid
          >
            {translate("FILTER_MODAL_APPLY_BUTTON")}
          </Button>
        </div>
      </div>
    </div>
  );
}
